/* eslint-disable no-unused-vars */
import React from 'react';
import { TrainerDisplayWrapper } from './trainer-wrapper/index';
import get from 'lodash.get';
import {
  durationLabel,
  lessThanMinutes,
  rangeOfMinutes,
  greaterThanMinutes,
  intensityLabel,
  durationLabelPlural,
  trainerLabel,
  trainerLabelPlural,
  intensityLabelPlural,
  locationLabelPlural,
  locationLabel,
  targetAreaLabel,
  targetAreaLabelPlural,
  continentLabel,
  continentLabelPlural,
  completedLabel,
  completedLabelPlural,
  workoutTypeLabel,
  workoutTypeLabelPlural,
  requiredEquipmentLabel,
  languageLabel,
  languageLabelPlural,
  onlyCompletedText,
  excludeCompletedText,
  requiredEquipmentLabelPlural,
  workoutSeriesTitle,
} from '../../../i18n/filter-translations';
import {
  workoutsTitle,
  seriesTitle,
  searchAllTypes,
} from '../../../i18n/translations';
import {
  translateTargetArea,
  translateContinent,
  translateLocationType,
  translateRequiredEquipment,
  translateIntensity,
} from '../../../i18n/filter-options';

export interface IFilterData {
  label: string;
  pluralLabel: string;
  dataLabel: string;
  singleValueFormatter?: (value: string) => string;
  options: Array<{ value: string; display: string | JSX.Element }>;
}

const duration = (): IFilterData => ({
  label: durationLabel(),
  pluralLabel: durationLabelPlural(),
  dataLabel: 'timeRanges',
  options: [
    {
      value: '0-1199',
      display: lessThanMinutes(20),
    },
    {
      value: '1200-1800',
      display: rangeOfMinutes(20, 30),
    },
    {
      value: '1800-2400',
      display: rangeOfMinutes(30, 40),
    },
    {
      value: '2400-3000',
      display: rangeOfMinutes(40, 50),
    },
    {
      value: '3000-100000',
      display: greaterThanMinutes(50),
    },
  ],
});

const seriesWorkoutSelector = (): IFilterData => ({
  label: workoutSeriesTitle(),
  pluralLabel: searchAllTypes(),
  dataLabel: 'includedEntities',
  options: [
    {
      value: 'workout',
      display: workoutsTitle(),
    },
    {
      value: 'program',
      display: seriesTitle(),
    },
  ],
});

// const emptyDistance: IFilterData = {
//   label: 'Distance',
//   dataLabel: 'distanceRanges',
//   options: [],
// };

// const distance = (equipmentType): IFilterData => {
//   switch (equipmentType.toLowerCase()) {
//     case 'bike':
//     case 'stationary bike':
//     case 'studio bike':
//     case 'tdf bike':
//       return {
//         label: emptyDistance.label,
//         dataLabel: emptyDistance.dataLabel,
//         options: [
//           {
//             value: 'lessthan3m',
//             display: 'Less than 3 mi',
//           },
//           {
//             value: '3to6m',
//             display: '3-6 mi',
//           },
//           {
//             value: '6to9m',
//             display: '6-9 mi',
//           },
//           {
//             value: '9to12m',
//             display: '9-12 mi',
//           },
//           {
//             value: '12to15m',
//             display: '12-15 mi',
//           },
//           {
//             value: '15plus',
//             display: '15+ mi',
//           },
//         ],
//       };
//     case 'rower':
//       return {
//         label: emptyDistance.label,
//         dataLabel: emptyDistance.dataLabel,
//         options: [
//           {
//             value: '1000to4000m',
//             display: '1,000-4,000 m',
//           },
//           {
//             value: '4000to6000m',
//             display: '4,000-6,000 m',
//           },
//           {
//             value: '6000to8000m',
//             display: '6,000-8,000 m',
//           },
//           {
//             value: '8000to10000m',
//             display: '8000-10,000 m',
//           },
//           {
//             value: '1000plus',
//             display: '10,000+ m',
//           },
//         ],
//       };
//     default:
//       return {
//         label: emptyDistance.label,
//         dataLabel: emptyDistance.dataLabel,
//         options: [
//           {
//             value: 'lessthan1m',
//             display: 'Less than 1 mi',
//           },
//           {
//             value: '1to3m',
//             display: '1-3 mi',
//           },
//           {
//             value: '3to5m',
//             display: '3-5 mi',
//           },
//           {
//             value: '5to7m',
//             display: '5-7 mi',
//           },
//           {
//             value: '7plus',
//             display: '7+',
//           },
//         ],
//       };
//   }
// };

// const calorieBurn: IFilterData = {
//   label: 'Calorie Burn',
//   dataLabel: 'calorieRanges',
//   options: [
//     {
//       value: 'lessthan250c',
//       display: 'Less than 250 Cals',
//     },
//     {
//       value: '250to350c',
//       display: '250-350 Cals',
//     },
//     {
//       value: '350to450c',
//       display: '350-450 Cals',
//     },
//     {
//       value: '450to550c',
//       display: '450-550 Cals',
//     },
//     {
//       value: '550to650c',
//       display: '550-650 Cals',
//     },
//     {
//       value: '650plusc',
//       display: '650+ Cals',
//     },
//   ],
// };

const trainer = (trainerData = []): IFilterData => ({
  label: trainerLabel(),
  pluralLabel: trainerLabelPlural(),
  dataLabel: 'trainerIds',
  singleValueFormatter: trainerId =>
    get(
      trainerData.find(trainer => trainer.id === trainerId),
      'name',
      'Trainer (1)',
    ),
  options: trainerData.map(trainer => ({
    value: trainer.id as string,
    display: <TrainerDisplayWrapper name={trainer.name} img={trainer.image} />,
  })),
});

const intensity = (): IFilterData => ({
  label: intensityLabel(),
  pluralLabel: intensityLabelPlural(),
  singleValueFormatter: level => translateIntensity(level),
  dataLabel: 'difficultyScores',
  options: ['beginner', 'intermediate', 'advanced'].map(level => {
    return {
      value: level,
      display: translateIntensity(level),
    };
  }),
});

const location = (locationOptions = []): IFilterData => ({
  label: locationLabel(),
  pluralLabel: locationLabelPlural(),
  dataLabel: 'locationTypes',
  options: locationOptions.map(location => ({
    value: location,
    display: translateLocationType(location),
  })),
});

const targetArea = (targetAreaOptions = []): IFilterData => ({
  label: targetAreaLabel(),
  pluralLabel: targetAreaLabelPlural(),
  dataLabel: 'targetAreas',
  options: targetAreaOptions.map(targetArea => ({
    value: targetArea,
    display: translateTargetArea(targetArea),
  })),
});

const continent = (continentOptions = []): IFilterData => ({
  label: continentLabel(),
  pluralLabel: continentLabelPlural(),
  dataLabel: 'continents',
  options: continentOptions.map(continent => ({
    value: continent,
    display: translateContinent(continent),
  })),
});

const workoutType = (categoryOptions = []): IFilterData => ({
  label: workoutTypeLabel(),
  pluralLabel: workoutTypeLabelPlural(),
  dataLabel: 'categories',
  options: categoryOptions.map(categoryOption => ({
    value: categoryOption.value,
    display: categoryOption.display,
  })),
});

const completed = (): IFilterData => ({
  label: completedLabel(),
  pluralLabel: completedLabelPlural(),
  dataLabel: 'hasCompleted',
  options: [
    { value: 'true', display: onlyCompletedText() },
    { value: 'false', display: excludeCompletedText() },
  ],
});

const requiredEquipment = (requiredEquipmentOptions = []): IFilterData => ({
  label: requiredEquipmentLabel(),
  pluralLabel: requiredEquipmentLabelPlural(),
  dataLabel: 'requiredEquipment',
  options: requiredEquipmentOptions.map(requiredEquipment => ({
    value: requiredEquipment,
    display: translateRequiredEquipment(requiredEquipment),
  })),
});

const language = (languageOptions = []): IFilterData => ({
  label: languageLabel(),
  pluralLabel: languageLabelPlural(),
  dataLabel: 'languages',
  options: languageOptions.map(languageOption => ({
    value: languageOption.value,
    display: languageOption.display,
  })),
});

interface IFilterArray {
  trainers: string[];
  categories: string[];
  categoriesTranslated: string[];
  categoryOptions: [];
  targetAreas: string[];
  continents: string[];
  locations: string[];
  requiredEquipment: string[];
  languages: string[];
  languagesTranslated: string[];
  languageOptions: [];
}

// Some options are commented out because they are not yet implemented in the service.
const getFilterData = (
  filterOptions: IFilterArray = {} as IFilterArray,
): IFilterData[] => {
  return [
    seriesWorkoutSelector(),
    duration(),
    trainer(filterOptions.trainers),
    workoutType(filterOptions.categoryOptions),
    intensity(),
    targetArea(filterOptions.targetAreas),
    continent(filterOptions.continents),
    location(filterOptions.locations),
    requiredEquipment(filterOptions.requiredEquipment),
    completed(),
    language(filterOptions.languageOptions),
  ];
};

export default getFilterData;
